import { useState, useEffect } from "react";
import { Service } from "@/types/dashboard";
import { ServiceList } from "@/components/dashboard/service-list/ServiceList";
import { ServicePreviewDialog } from "@/components/dashboard/service-list/ServicePreviewDialog";
import { ServiceFormDialog } from "@/components/service-form/ServiceFormDialog";
import { useServiceImageHandler } from "./services/ServiceImageHandler";
import { useServiceActions } from "./services/useServiceActions";
import { ServiceHeader } from "./services/ServiceHeader";
import { ServiceEmptyState } from "./services/ServiceEmptyState";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingTutorialTooltip } from "./services/OnboardingTutorialTooltip";
import { useLocation } from "react-router-dom";

interface ServicesSectionProps {
  services: Service[];
  searchQuery: string;
  userCategories: any[];
  setSearchQuery: (query: string) => void;
  onDeleteService: (serviceId: string) => Promise<boolean>;
  onUpdateServiceCategory: (serviceId: string, categoryId: string, newUserCategories: any[]) => Promise<boolean>;
  currency?: string | null;
  refreshCategories: () => Promise<void>;
}

export const ServicesSection = ({
  services,
  searchQuery,
  userCategories,
  setSearchQuery,
  onDeleteService,
  onUpdateServiceCategory,
  currency,
  refreshCategories
}: ServicesSectionProps) => {
  const [localServices, setLocalServices] = useState<Service[]>(services);
  const [localUserCategories, setLocalUserCategories] = useState<any[]>(userCategories);
  const location = useLocation();
  const [showOnboardingTooltip, setShowOnboardingTooltip] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [serviceImagesMap, setServiceImagesMap] = useState<{[key: string]: any[]}>({});

  // Check if user is coming from onboarding by checking the referrer
  useEffect(() => {
    // Use sessionStorage to ensure the tooltip only shows once per session
    const hasShownTooltip = sessionStorage.getItem('onboardingTooltipShown');
    const fromOnboarding = document.referrer.includes('/simple-onboarding');
    
    if (!hasShownTooltip && (location.key === 'default' || fromOnboarding)) {
      setShowOnboardingTooltip(true);
    }
  }, [location]);
  
  const handleDismissTooltip = () => {
    setShowOnboardingTooltip(false);
    sessionStorage.setItem('onboardingTooltipShown', 'true');
  };

  // Update local services when the services prop changes
  useEffect(() => {
    console.log("Services prop updated in ServicesSection:", services.length);
    setLocalServices(services);
    // Fetch images for all services
    fetchAllServiceImages(services);
  }, [services]);

  // Update local categories when userCategories prop changes
  useEffect(() => {
    setLocalUserCategories(userCategories);
  }, [userCategories]);

  // Function to fetch images for all services
  const fetchAllServiceImages = async (services: Service[]) => {
    try {
      const imagesMap: {[key: string]: any[]} = {};
      
      for (const service of services) {
        console.log(`Fetching images for service ${service.id}:`, {
          serviceName: service.name,
          coverImage: service.image_url
        });

        // Fetch additional images for the service
        const { data: additionalImages, error } = await supabase
          .from('service_images')
          .select('id, image_url, sequence')
          .eq('service_id', service.id)
          .order('sequence', { ascending: true });

        if (error) {
          console.error(`Error fetching images for service ${service.id}:`, error);
          continue;
        }

        console.log(`Additional images for service ${service.id}:`, additionalImages);

        const allImages = [];
        
        // Add cover image if it exists
        if (service.image_url && service.image_url.trim() !== '') {
          allImages.push({
            id: 'cover',
            image_url: service.image_url
          });
        }

        // Add additional images
        if (additionalImages && additionalImages.length > 0) {
          const nonDuplicateImages = additionalImages.filter(
            img => !service.image_url || img.image_url !== service.image_url
          );
          allImages.push(...nonDuplicateImages);
        }

        console.log(`Final images for service ${service.id}:`, allImages);
        imagesMap[service.id] = allImages;
      }

      console.log('Final serviceImagesMap:', imagesMap);
      setServiceImagesMap(imagesMap);
    } catch (error) {
      console.error('Error fetching service images:', error);
    }
  };

  // Custom hooks for service functionality
  const {
    isDeleting,
    previewService,
    setPreviewService,
    selectedService,
    serviceFormOpen,
    setServiceFormOpen,
    isLoadingService,
    handleToggleVisibility,
    handleEditService,
    handleAddNewService,
    handleDeleteService,
    handleServiceFormSuccess
  } = useServiceActions(setLocalServices, onDeleteService, refreshServices);

  const {
    serviceImages,
    selectedImageIndex,
    loadServiceImages,
    handleImageNavigation
  } = useServiceImageHandler(previewService?.id || '');

  // Function to refresh services
  async function refreshServices(): Promise<void> {
    try {
      if (!isRefreshing) {
        setIsRefreshing(true);
        console.log('Starting service refresh in ServicesSection...');
        
        const { fetchServices } = await import('@/utils/serviceUtils');
        const updatedServices = await fetchServices();
        console.log('Services fetched successfully in ServicesSection:', updatedServices.length);
        
        setLocalServices(updatedServices);
        await fetchAllServiceImages(updatedServices);
      }
    } catch (error) {
      console.error('Error refreshing services in ServicesSection:', error);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }

  const handlePreviewService = async (service: Service) => {
    setPreviewService(service);
    await loadServiceImages(service.id);
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100 relative">
      <ServiceHeader onAddNewService={handleAddNewService} />

      {localServices.length > 0 ? (
        <>
          <ServiceList 
            services={localServices} 
            userCategories={localUserCategories}
            onUpdateServiceCategory={async (serviceId, categoryId, newUserCategories) => {
              const success = await onUpdateServiceCategory(serviceId, categoryId, newUserCategories);
              if (success) {
                // Refresh services to get updated category info
                await refreshServices();
                // If the category list was changed (e.g., a new one added), refresh it globally
                if (newUserCategories.length !== userCategories.length) {
                  await refreshCategories();
                }
              }
            }}
            onEditService={handleEditService}
            onDeleteService={handleDeleteService}
            onPreviewService={handlePreviewService}
            onToggleVisibility={handleToggleVisibility}
            isDeleting={isDeleting}
            currency={currency}
            serviceImagesMap={serviceImagesMap}
            onUserCategoriesUpdate={setLocalUserCategories}
          />
          {showOnboardingTooltip && localServices.length > 0 && (
            <div className="absolute top-[110px] right-[100px]">
              <OnboardingTutorialTooltip 
                isVisible={showOnboardingTooltip}
                onDismiss={handleDismissTooltip}
              />
            </div>
          )}
        </>
      ) : (
        <ServiceEmptyState onAddNewService={handleAddNewService} />
      )}

      {previewService && (
        <ServicePreviewDialog
          service={previewService}
          open={!!previewService}
          onOpenChange={() => setPreviewService(null)}
          serviceImages={serviceImages}
          selectedImageIndex={selectedImageIndex}
          onImageNavigation={handleImageNavigation}
          businessName={""} 
          currency={currency}
        />
      )}

      <ServiceFormDialog
        open={serviceFormOpen}
        onOpenChange={setServiceFormOpen}
        onSuccess={handleServiceFormSuccess}
        initialData={selectedService}
        refreshServices={refreshServices}
        isLoading={isLoadingService}
      />
    </div>
  );
};
